import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import Map, {
  Layer,
  Marker,
  NavigationControl,
  Popup,
  Source,
  type MapRef,
} from 'react-map-gl/maplibre';
import type { StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { circlePolygon } from '@/lib/geo';
import {
  AlertCircle,
  Check,
  Eye,
  Info,
  Layers,
  ListFilter,
  Locate,
  Lock,
  Loader2,
  MapIcon as MapExternal,
  MapPin,
  Navigation,
  RefreshCw,
  RotateCcw,
  Search,
  Terminal,
  Unlock,
  X,
} from 'lucide-react';
import { commandsApi, GT06_COMMANDS } from '@/api/endpoints/commands';
import { geofencesApi } from '@/api/endpoints/geofences';
import { ApiError } from '@/api/client';
import { useCommandsStore } from '@/stores/commands';
import { useQuery } from '@tanstack/react-query';
import { areaToGeoJSON, parseWkt } from '@/lib/wkt';
import { extractUF, formatAddress } from '@/lib/address';
import { Input } from '@/components/ui/input';
import { useFleetStore } from '@/stores/fleet';
import { StatusBadge } from '@/components/device/StatusBadge';
import { DevicePhoto } from '@/components/device/DevicePhoto';
import {
  computeMachineStatus,
  getDeviceNickname,
  isBlocked,
  readTelemetry,
  STATUS_ORDER,
  type MachineStatus,
} from '@/lib/machineStatus';
import { TelemetryIcons } from '@/components/device/TelemetryIcons';
import {
  cn,
  formatCoords,
  formatDateTime,
  formatRelativeTime,
  formatSpeed,
} from '@/lib/utils';
import type { Device, Position } from '@/api/types';

const STREETS_STYLE = 'https://tiles.openfreemap.org/styles/bright';

const SATELLITE_STYLE: StyleSpecification = {
  version: 8,
  sources: {
    esri: {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      attribution: 'Tiles © Esri',
    },
    labels: {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
    },
  },
  layers: [
    { id: 'esri', type: 'raster', source: 'esri' },
    { id: 'labels', type: 'raster', source: 'labels' },
  ],
};

const STATUS_HEX: Record<MachineStatus, string> = {
  running: '#34C759',
  ready: '#FF9500',
  blocked: '#FF3B30',
  off: '#8E8E93',
};

const MARKER_SRC: Record<MachineStatus, string> = {
  running: '/markers/marker-running.png',
  ready: '/markers/marker-ready.png',
  blocked: '/markers/marker-blocked.png',
  off: '/markers/marker-off.png',
};

const SHORT_LABEL: Record<MachineStatus, string> = {
  running: 'Ativas',
  ready: 'Prontas',
  blocked: 'Bloq.',
  off: 'Deslig.',
};

interface FleetItem {
  device: Device;
  position: Position;
  status: MachineStatus;
  uf: string | null;
}

type StatusFilter = 'all' | MachineStatus;
type UfFilter = 'all' | 'unknown' | string;
type BasemapKind = 'streets' | 'satellite';

export function MapPage() {
  const devices = useFleetStore((s) => s.devices);
  const positions = useFleetStore((s) => s.positions);
  const mapRef = useRef<MapRef | null>(null);

  const [searchParams] = useSearchParams();
  const queryDevice = searchParams.get('device');
  const focusedFromQueryRef = useRef<string | null>(null);

  const [selectedId, setSelectedId] = useState<number | null>(
    queryDevice ? Number(queryDevice) : null,
  );
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [ufFilter, setUfFilter] = useState<UfFilter>('all');
  const [panelOpenMobile, setPanelOpenMobile] = useState(false);
  const [basemap, setBasemap] = useState<BasemapKind>('streets');
  const [panelHidden, setPanelHidden] = useState(false);

  const items = useMemo<FleetItem[]>(() => {
    const list: FleetItem[] = [];
    for (const device of devices.values()) {
      const position = positions.get(device.id);
      if (
        !position ||
        position.latitude == null ||
        position.longitude == null ||
        Number.isNaN(position.latitude) ||
        Number.isNaN(position.longitude)
      ) {
        continue;
      }
      list.push({
        device,
        position,
        status: computeMachineStatus(device, position),
        uf: extractUF(position.address),
      });
    }
    return list;
  }, [devices, positions]);

  const counts = useMemo(() => {
    const c: Record<MachineStatus, number> = {
      running: 0,
      ready: 0,
      blocked: 0,
      off: 0,
    };
    for (const it of items) c[it.status] += 1;
    return c;
  }, [items]);

  const ufOptions = useMemo(() => {
    const counter: Record<string, number> = {};
    let unknownCount = 0;
    for (const it of items) {
      if (it.uf) counter[it.uf] = (counter[it.uf] ?? 0) + 1;
      else unknownCount += 1;
    }
    const list = Object.entries(counter).sort((a, b) =>
      a[0].localeCompare(b[0]),
    ) as [string, number][];
    return { list, unknownCount };
  }, [items]);

  const visibleItems = useMemo(() => {
    let list = items;
    if (statusFilter !== 'all') list = list.filter((it) => it.status === statusFilter);
    if (ufFilter === 'unknown') list = list.filter((it) => !it.uf);
    else if (ufFilter !== 'all') list = list.filter((it) => it.uf === ufFilter);
    return list;
  }, [items, statusFilter, ufFilter]);

  const filteredListItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = visibleItems;
    if (q) {
      list = list.filter(
        (it) =>
          it.device.name.toLowerCase().includes(q) ||
          it.device.uniqueId.toLowerCase().includes(q) ||
          (getDeviceNickname(it.device)?.toLowerCase().includes(q) ?? false),
      );
    }
    return [...list].sort((a, b) => {
      const ta = new Date(a.position.fixTime).getTime();
      const tb = new Date(b.position.fixTime).getTime();
      return tb - ta;
    });
  }, [visibleItems, search]);

  const initialView = useMemo(() => {
    if (items.length === 0) return { longitude: -47, latitude: -15, zoom: 3.5 };
    let lonSum = 0;
    let latSum = 0;
    for (const it of items) {
      lonSum += it.position.longitude!;
      latSum += it.position.latitude!;
    }
    return {
      longitude: lonSum / items.length,
      latitude: latSum / items.length,
      zoom: 4,
    };
  }, [items]);

  const selected = useMemo(
    () => (selectedId != null ? items.find((it) => it.device.id === selectedId) : undefined),
    [items, selectedId],
  );

  const accuracyCircle = useMemo(() => {
    if (!selected) return null;
    return circlePolygon(selected.position.longitude!, selected.position.latitude!, 50);
  }, [selected]);

  const geofencesQuery = useQuery({
    queryKey: ['geofences'],
    queryFn: () => geofencesApi.list(),
    staleTime: 60_000,
  });

  const geofencesGeoJson = useMemo(() => {
    const features = (geofencesQuery.data ?? [])
      .map((g) => {
        const area = parseWkt(g.area);
        if (!area) return null;
        const feature = areaToGeoJSON(area);
        const a = g.attributes ?? {};
        const color =
          typeof a.color === 'string' && (a.color as string).trim()
            ? (a.color as string)
            : '#0A84FF';
        feature.properties = {
          id: g.id,
          name: g.name,
          color,
          isLine: area.type === 'LINESTRING',
        };
        return feature;
      })
      .filter((f): f is GeoJSON.Feature<GeoJSON.Geometry> => f !== null);
    return {
      type: 'FeatureCollection' as const,
      features,
    };
  }, [geofencesQuery.data]);

  const focusOn = (item: FleetItem) => {
    setSelectedId(item.device.id);
    mapRef.current?.flyTo({
      center: [item.position.longitude!, item.position.latitude!],
      zoom: 14,
      duration: 800,
    });
    setPanelOpenMobile(false);
  };

  // Quando a página é aberta com ?device=<id>, dá flyTo na máquina assim que
  // os dados estiverem disponíveis. Usa ref pra disparar uma única vez.
  useEffect(() => {
    if (!queryDevice) return;
    if (focusedFromQueryRef.current === queryDevice) return;
    const id = Number(queryDevice);
    const item = items.find((it) => it.device.id === id);
    if (item) {
      focusOn(item);
      focusedFromQueryRef.current = queryDevice;
    }
  }, [queryDevice, items]);

  const recenterAll = () => {
    if (visibleItems.length === 0) return;
    if (visibleItems.length === 1) {
      const it = visibleItems[0];
      mapRef.current?.flyTo({
        center: [it.position.longitude!, it.position.latitude!],
        zoom: 14,
        duration: 600,
      });
      return;
    }
    let minLng = Infinity;
    let minLat = Infinity;
    let maxLng = -Infinity;
    let maxLat = -Infinity;
    for (const it of visibleItems) {
      minLng = Math.min(minLng, it.position.longitude!);
      minLat = Math.min(minLat, it.position.latitude!);
      maxLng = Math.max(maxLng, it.position.longitude!);
      maxLat = Math.max(maxLat, it.position.latitude!);
    }
    mapRef.current?.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: 80, duration: 600, maxZoom: 14 },
    );
  };

  return (
    <div className="relative flex h-full">
      {!panelHidden && (
        <DeviceListPanel
          items={filteredListItems}
          total={items.length}
          counts={counts}
          ufOptions={ufOptions}
          ufFilter={ufFilter}
          onUfFilterChange={setUfFilter}
          search={search}
          onSearchChange={setSearch}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          selectedId={selectedId}
          onSelect={focusOn}
          openMobile={panelOpenMobile}
          onCloseMobile={() => setPanelOpenMobile(false)}
        />
      )}

      <div className="relative flex-1">
        <Map
          ref={mapRef}
          initialViewState={initialView}
          mapStyle={basemap === 'streets' ? STREETS_STYLE : SATELLITE_STYLE}
          style={{ width: '100%', height: '100%' }}
          attributionControl={false}
        >
          <NavigationControl position="top-right" showCompass={false} />

          {geofencesGeoJson.features.length > 0 && (
            <Source id="geofences" type="geojson" data={geofencesGeoJson}>
              <Layer
                id="geofences-fill"
                type="fill"
                filter={['!=', ['get', 'isLine'], true]}
                paint={{
                  'fill-color': ['get', 'color'],
                  'fill-opacity': 0.12,
                }}
              />
              <Layer
                id="geofences-line"
                type="line"
                paint={{
                  'line-color': ['get', 'color'],
                  'line-width': ['case', ['==', ['get', 'isLine'], true], 3, 2],
                  'line-opacity': 0.8,
                }}
              />
            </Source>
          )}

          {accuracyCircle && (
            <Source id="accuracy-circle" type="geojson" data={accuracyCircle}>
              <Layer
                id="accuracy-circle-fill"
                type="fill"
                paint={{
                  'fill-color': '#0A84FF',
                  'fill-opacity': 0.18,
                }}
              />
              <Layer
                id="accuracy-circle-outline"
                type="line"
                paint={{
                  'line-color': '#0A84FF',
                  'line-width': 2,
                  'line-opacity': 0.7,
                }}
              />
            </Source>
          )}

          {visibleItems.map((it) => (
            <Marker
              key={it.device.id}
              longitude={it.position.longitude!}
              latitude={it.position.latitude!}
              anchor="bottom"
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                focusOn(it);
              }}
            >
              <button
                type="button"
                className={cn(
                  'flex items-end justify-center transition-transform hover:scale-110',
                  selectedId === it.device.id && 'scale-125',
                )}
                title={it.device.name}
              >
                <img
                  src={MARKER_SRC[it.status]}
                  alt=""
                  className={cn(
                    'pointer-events-none select-none',
                    selectedId === it.device.id ? 'h-12 w-12' : 'h-9 w-9',
                    'drop-shadow-md',
                    it.status === 'off' && 'grayscale',
                  )}
                  draggable={false}
                />
              </button>
            </Marker>
          ))}

          {selected && (
            <Popup
              longitude={selected.position.longitude!}
              latitude={selected.position.latitude!}
              anchor="bottom"
              offset={36}
              closeButton
              closeOnClick={false}
              onClose={() => setSelectedId(null)}
              maxWidth="380px"
            >
              <PopupContent {...selected} />
            </Popup>
          )}
        </Map>

        <FilterBar
          counts={counts}
          total={items.length}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
        />

        <button
          type="button"
          onClick={() => setPanelOpenMobile(true)}
          className="absolute left-3 top-16 z-10 inline-flex items-center gap-1.5 rounded-full bg-card px-3 py-2 text-xs font-semibold shadow ring-1 ring-border lg:hidden"
        >
          <ListFilter className="h-4 w-4" />
          Lista · {filteredListItems.length}
        </button>

        <FabStack
          basemap={basemap}
          onToggleBasemap={() => setBasemap((b) => (b === 'streets' ? 'satellite' : 'streets'))}
          onRecenter={recenterAll}
          onTogglePanel={() => setPanelHidden((v) => !v)}
          panelHidden={panelHidden}
        />
      </div>
    </div>
  );
}

function FilterBar({
  counts,
  total,
  statusFilter,
  onStatusFilterChange,
}: {
  counts: Record<MachineStatus, number>;
  total: number;
  statusFilter: StatusFilter;
  onStatusFilterChange: (s: StatusFilter) => void;
}) {
  return (
    <div className="absolute left-1/2 top-3 z-10 flex max-w-[calc(100%-1rem)] -translate-x-1/2 flex-wrap items-center justify-center gap-1 rounded-full bg-card/95 px-2 py-1 shadow ring-1 ring-border backdrop-blur">
      <FilterChip
        label={`Todos · ${total}`}
        active={statusFilter === 'all'}
        onClick={() => onStatusFilterChange('all')}
      />
      {STATUS_ORDER.map((s) => (
        <FilterChip
          key={s}
          label={`${SHORT_LABEL[s]} ${counts[s]}`}
          color={STATUS_HEX[s]}
          active={statusFilter === s}
          onClick={() => onStatusFilterChange(s)}
        />
      ))}
    </div>
  );
}

function FabStack({
  basemap,
  onToggleBasemap,
  onRecenter,
  onTogglePanel,
  panelHidden,
}: {
  basemap: BasemapKind;
  onToggleBasemap: () => void;
  onRecenter: () => void;
  onTogglePanel: () => void;
  panelHidden: boolean;
}) {
  return (
    <div className="absolute right-3 top-32 z-10 flex flex-col gap-2">
      <FabButton
        onClick={onToggleBasemap}
        title={basemap === 'streets' ? 'Mostrar satélite' : 'Mostrar mapa'}
        active={basemap === 'satellite'}
      >
        <Layers className="h-4 w-4" />
        <span className="hidden text-[11px] font-bold sm:inline">
          {basemap === 'streets' ? 'Satélite' : 'Mapa'}
        </span>
      </FabButton>
      <FabButton onClick={onRecenter} title="Centralizar na frota">
        <Locate className="h-4 w-4" />
      </FabButton>
      <FabButton
        onClick={onTogglePanel}
        title={panelHidden ? 'Mostrar lista' : 'Esconder lista'}
        className="hidden lg:inline-flex"
      >
        <ListFilter className="h-4 w-4" />
      </FabButton>
      <FabButton onClick={() => window.location.reload()} title="Recarregar">
        <RefreshCw className="h-4 w-4" />
      </FabButton>
    </div>
  );
}

function FabButton({
  children,
  onClick,
  title,
  active,
  className,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  active?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-full bg-card px-3 py-2 shadow-lg ring-1 ring-border transition-colors hover:bg-accent',
        active && 'bg-primary text-primary-foreground hover:bg-primary/90',
        className,
      )}
    >
      {children}
    </button>
  );
}

function FilterChip({
  label,
  color,
  active,
  onClick,
}: {
  label: string;
  color?: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all',
        active
          ? 'bg-secondary text-foreground ring-1 ring-border'
          : 'text-muted-foreground hover:bg-accent',
      )}
    >
      {color && (
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
      )}
      {label}
    </button>
  );
}

function DeviceListPanel({
  items,
  total,
  counts,
  ufOptions,
  ufFilter,
  onUfFilterChange,
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  selectedId,
  onSelect,
  openMobile,
  onCloseMobile,
}: {
  items: FleetItem[];
  total: number;
  counts: Record<MachineStatus, number>;
  ufOptions: { list: [string, number][]; unknownCount: number };
  ufFilter: UfFilter;
  onUfFilterChange: (v: UfFilter) => void;
  search: string;
  onSearchChange: (v: string) => void;
  statusFilter: StatusFilter;
  onStatusFilterChange: (s: StatusFilter) => void;
  selectedId: number | null;
  onSelect: (it: FleetItem) => void;
  openMobile: boolean;
  onCloseMobile: () => void;
}) {
  return (
    <>
      {openMobile && (
        <div
          className="fixed inset-0 z-30 bg-background/40 lg:hidden"
          onClick={onCloseMobile}
          aria-hidden
        />
      )}
      <aside
        className={cn(
          'flex w-[320px] shrink-0 flex-col border-r border-border bg-card',
          'fixed inset-y-0 left-0 z-40 transition-transform lg:static lg:translate-x-0',
          openMobile ? 'translate-x-0 shadow-xl' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <header className="space-y-3 border-b border-border p-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold">
              Máquinas <span className="text-muted-foreground">· {items.length}/{total}</span>
            </h2>
            <button
              type="button"
              onClick={onCloseMobile}
              className="rounded-md p-1 text-muted-foreground hover:bg-accent lg:hidden"
              aria-label="Fechar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Buscar"
              className="rounded-full bg-secondary pl-9"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <FilterChip
              label="Todos"
              active={statusFilter === 'all'}
              onClick={() => onStatusFilterChange('all')}
            />
            {STATUS_ORDER.map((s) => (
              <FilterChip
                key={s}
                label={`${SHORT_LABEL[s]} ${counts[s]}`}
                color={STATUS_HEX[s]}
                active={statusFilter === s}
                onClick={() => onStatusFilterChange(s)}
              />
            ))}
          </div>
          <label
            className={cn(
              'inline-flex w-fit items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold',
              ufFilter !== 'all'
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground',
            )}
          >
            <span>UF</span>
            <select
              value={ufFilter}
              onChange={(e) => onUfFilterChange(e.target.value)}
              className="cursor-pointer bg-transparent outline-none"
            >
              <option value="all">Todas · {total}</option>
              {ufOptions.list.map(([uf, count]) => (
                <option key={uf} value={uf}>
                  {uf} · {count}
                </option>
              ))}
              {ufOptions.unknownCount > 0 && (
                <option value="unknown">Indefinida · {ufOptions.unknownCount}</option>
              )}
            </select>
          </label>
        </header>
        <ul className="flex-1 divide-y divide-border overflow-y-auto">
          {items.length === 0 ? (
            <li className="p-4 text-center text-xs text-muted-foreground">
              Nenhuma máquina nesse filtro.
            </li>
          ) : (
            items.map((it) => (
              <DeviceItem
                key={it.device.id}
                item={it}
                active={selectedId === it.device.id}
                onClick={() => onSelect(it)}
              />
            ))
          )}
        </ul>
      </aside>
    </>
  );
}

function DeviceItem({
  item,
  active,
  onClick,
}: {
  item: FleetItem;
  active: boolean;
  onClick: () => void;
}) {
  const { device, position, status } = item;
  const nickname = getDeviceNickname(device);
  const telemetry = readTelemetry(position);
  const blocked = isBlocked(device, position);
  const speedKmh = Math.round((position.speed ?? 0) * 1.852);
  const hasTelemetry =
    telemetry.ignition !== undefined ||
    telemetry.batteryLevel !== undefined ||
    telemetry.battery !== undefined ||
    telemetry.blocked ||
    telemetry.alarm ||
    telemetry.charging;

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex w-full items-start gap-3 p-3 text-left transition-colors',
          active ? 'bg-primary/10' : 'hover:bg-accent',
        )}
      >
        <DevicePhoto device={device} size="md" />
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-sm font-bold">{device.name}</span>
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ background: STATUS_HEX[status] }}
              title={status}
            />
          </div>
          {nickname && (
            <p className="truncate text-xs font-medium text-foreground/70">{nickname}</p>
          )}
          {device.model && (
            <p className="truncate text-xs text-muted-foreground">{device.model}</p>
          )}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 pt-0.5 text-[11px]">
            <span
              className="rounded px-1.5 font-semibold"
              style={{ background: `${STATUS_HEX[status]}1A`, color: STATUS_HEX[status] }}
            >
              {speedKmh} km/h
            </span>
            {blocked && (
              <span className="rounded bg-status-blocked/10 px-1.5 font-semibold text-status-blocked">
                Bloqueada
              </span>
            )}
            <span className="text-muted-foreground">{formatRelativeTime(position.fixTime)}</span>
          </div>
          {position.address && (
            <p className="line-clamp-1 text-[11px] text-foreground/70">{formatAddress(position.address)}</p>
          )}
          {hasTelemetry && (
            <div className="pt-1">
              <TelemetryIcons telemetry={telemetry} />
            </div>
          )}
        </div>
      </button>
    </li>
  );
}

function PopupContent({ device, position, status }: FleetItem) {
  const nickname = getDeviceNickname(device);
  const blocked = isBlocked(device, position);
  const speedKnots = position.speed ?? 0;
  const lat = position.latitude!;
  const lon = position.longitude!;
  const [commandOpen, setCommandOpen] = useState(false);

  const mapsUrl = `https://www.google.com/maps?q=${lat},${lon}`;
  const streetViewUrl = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lon}`;

  return (
    <div className="space-y-3 p-1 text-sm">
      <div className="flex items-start gap-3">
        <DevicePhoto device={device} size="md" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-bold">{device.name}</p>
          {nickname && <p className="truncate text-xs font-medium text-foreground/70">{nickname}</p>}
          {device.model && (
            <p className="truncate text-xs text-muted-foreground">{device.model}</p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={status} />
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold',
            blocked
              ? 'bg-status-blocked/10 text-status-blocked'
              : 'bg-status-running/10 text-status-running',
          )}
        >
          {blocked ? 'Bloqueada' : 'Desbloqueada'}
        </span>
      </div>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
        <div>
          <dt className="text-muted-foreground">Velocidade</dt>
          <dd className="font-semibold">{formatSpeed(speedKnots)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Atualizado</dt>
          <dd className="font-semibold">{formatRelativeTime(position.fixTime)}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-muted-foreground">Coordenadas</dt>
          <dd className="font-mono text-[11px]">{formatCoords(lat, lon)}</dd>
        </div>
      </dl>

      {position.address && (
        <p className="flex items-start gap-1.5 text-xs text-foreground/80">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
          <span>{formatAddress(position.address)}</span>
        </p>
      )}

      <p className="text-[10px] text-muted-foreground">
        Hora exata: {formatDateTime(position.fixTime)}
      </p>

      <div className="grid grid-cols-4 gap-1.5 pt-1">
        <PopupAction icon={Info} label="Detalhe" to={`/devices/${device.id}`} />
        <PopupAction
          icon={MapExternal}
          label="Maps"
          href={mapsUrl}
          color="text-status-ready"
        />
        <PopupAction icon={Eye} label="Ver" href={streetViewUrl} color="text-primary" />
        <PopupAction
          icon={Terminal}
          label="Comandos"
          onClick={() => setCommandOpen(true)}
          color="text-status-blocked"
        />
      </div>

      {commandOpen && <CommandModal device={device} onClose={() => setCommandOpen(false)} />}
    </div>
  );
}

function PopupAction({
  icon: Icon,
  label,
  to,
  href,
  onClick,
  color = 'text-primary',
}: {
  icon: typeof Info;
  label: string;
  to?: string;
  href?: string;
  onClick?: () => void;
  color?: string;
}) {
  const className = cn(
    'flex flex-col items-center justify-center gap-1 rounded-lg bg-primary/5 px-1 py-2 text-[10px] font-semibold transition-colors hover:bg-primary/15',
    color,
  );
  const inner = (
    <>
      <Icon className="h-4 w-4" />
      {label}
    </>
  );
  if (to) return <Link to={to} className={className}>{inner}</Link>;
  if (href)
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {inner}
      </a>
    );
  return (
    <button type="button" onClick={onClick} className={className}>
      {inner}
    </button>
  );
}

interface CommandSpec {
  key: keyof typeof GT06_COMMANDS;
  label: string;
  icon: typeof Lock;
  color: string;
  bg: string;
}

const COMMAND_SPECS: CommandSpec[] = [
  {
    key: 'block',
    label: 'Bloquear',
    icon: Lock,
    color: 'text-status-blocked',
    bg: 'bg-status-blocked/10',
  },
  {
    key: 'unblock',
    label: 'Desbloquear',
    icon: Unlock,
    color: 'text-status-running',
    bg: 'bg-status-running/10',
  },
  {
    key: 'reset',
    label: 'Reset',
    icon: RotateCcw,
    color: 'text-status-ready',
    bg: 'bg-status-ready/10',
  },
  {
    key: 'position',
    label: 'Forçar Posição',
    icon: Navigation,
    color: 'text-primary',
    bg: 'bg-primary/10',
  },
];

function CommandModal({ device, onClose }: { device: Device; onClose: () => void }) {
  const [pendingKey, setPendingKey] = useState<CommandSpec['key'] | null>(null);
  const [feedback, setFeedback] = useState<
    | { kind: 'success'; label: string; data: string; uid: string }
    | { kind: 'error'; label: string; message: string }
    | null
  >(null);
  const pushCmd = useCommandsStore((s) => s.push);
  const failCmd = useCommandsStore((s) => s.fail);
  const deviceCommands = useCommandsStore((s) =>
    s.records.filter((r) => r.deviceId === device.id),
  );

  const mutation = useMutation({
    mutationFn: ({ data }: { key: CommandSpec['key']; label: string; data: string }) =>
      commandsApi.send(device.id, data),
    onSuccess: (_d, vars) => {
      const uid = pushCmd({
        deviceId: device.id,
        label: vars.label,
        data: vars.data,
        sentAt: Date.now(),
      });
      setFeedback({ kind: 'success', label: vars.label, data: vars.data, uid });
      setPendingKey(null);
    },
    onError: (err, vars) => {
      let msg = 'Falha ao enviar comando.';
      if (err instanceof ApiError) {
        msg = `Servidor respondeu ${err.status} (${err.statusText || 'erro'}).`;
      } else if (err instanceof Error) {
        msg = err.message;
      }
      setFeedback({ kind: 'error', label: vars.label, message: msg });
      setPendingKey(null);
    },
  });

  const sendCommand = (spec: CommandSpec) => {
    setFeedback(null);
    setPendingKey(spec.key);
    mutation.mutate({ key: spec.key, label: spec.label, data: GT06_COMMANDS[spec.key] });
  };
  void failCmd;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-xl bg-card p-5 shadow-xl ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-base font-bold">Comandos</h3>
            <p className="truncate text-xs text-muted-foreground">{device.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mb-3 text-[11px] text-muted-foreground">
          Padrão GT06. O comando é enviado pelo canal TCP que o servidor mantém com a máquina.
        </p>

        <div className="grid grid-cols-2 gap-2">
          {COMMAND_SPECS.map((spec) => (
            <CommandButton
              key={spec.key}
              spec={spec}
              loading={pendingKey === spec.key}
              disabled={mutation.isPending}
              onClick={() => sendCommand(spec)}
            />
          ))}
        </div>

        {feedback && (
          <div
            className={cn(
              'mt-4 flex items-start gap-2 rounded-md px-3 py-2 text-xs font-medium',
              feedback.kind === 'success'
                ? 'bg-status-running/10 text-status-running'
                : 'bg-status-blocked/10 text-status-blocked',
            )}
          >
            {feedback.kind === 'success' ? (
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            ) : (
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            )}
            <div className="space-y-0.5">
              <p className="font-semibold">
                {feedback.kind === 'success'
                  ? `Comando "${feedback.label}" enviado ao servidor`
                  : `Erro ao enviar "${feedback.label}"`}
              </p>
              <p className="font-mono text-[10px] opacity-80">
                {feedback.kind === 'success' ? feedback.data : feedback.message}
              </p>
              {feedback.kind === 'success' && (
                <p className="text-[10px] opacity-80">
                  Aguardando confirmação da máquina abaixo.
                </p>
              )}
            </div>
          </div>
        )}

        {deviceCommands.length > 0 && (
          <div className="mt-4">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Histórico recente
            </p>
            <ul className="max-h-48 divide-y divide-border overflow-y-auto rounded-md border border-border">
              {deviceCommands.slice(0, 10).map((r) => (
                <CommandHistoryRow key={r.uid} record={r} />
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function CommandHistoryRow({ record }: { record: import('@/stores/commands').CommandRecord }) {
  const sentRelative = formatRelativeTime(new Date(record.sentAt).toISOString());
  let statusLabel = 'Enviado, aguardando confirmação';
  let statusColor = 'text-status-ready';
  let StatusIcon: typeof Check = Loader2;
  if (record.status === 'confirmed') {
    statusLabel = 'Executado pela máquina';
    statusColor = 'text-status-running';
    StatusIcon = Check;
  } else if (record.status === 'failed') {
    statusLabel = `Falhou${record.result ? `: ${record.result}` : ''}`;
    statusColor = 'text-status-blocked';
    StatusIcon = AlertCircle;
  } else if (record.status === 'timeout') {
    statusLabel = 'Sem resposta da máquina (timeout)';
    statusColor = 'text-status-off';
    StatusIcon = AlertCircle;
  }
  return (
    <li className="flex items-start justify-between gap-2 px-3 py-2 text-[11px]">
      <div className="min-w-0">
        <p className="font-semibold">{record.label}</p>
        <p className="font-mono text-[10px] text-muted-foreground">{record.data}</p>
        <p className="text-muted-foreground">{sentRelative}</p>
      </div>
      <span className={cn('inline-flex items-center gap-1 font-semibold', statusColor)}>
        <StatusIcon
          className={cn('h-3 w-3', record.status === 'sent' && 'animate-spin')}
        />
        {statusLabel}
      </span>
    </li>
  );
}

function CommandButton({
  spec,
  loading,
  disabled,
  onClick,
}: {
  spec: CommandSpec;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex flex-col items-center gap-1.5 rounded-lg px-3 py-3 text-xs font-semibold transition-colors',
        spec.bg,
        spec.color,
        disabled ? 'opacity-50' : 'hover:opacity-80',
      )}
    >
      {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <spec.icon className="h-5 w-5" />}
      {spec.label}
    </button>
  );
}
